import Header from "../component/Header";
import {useDropzone} from 'react-dropzone';
import { useEffect, useRef, useState } from "react";
import { Box, Button, FormControl, FormHelperText, TextField, Typography, Backdrop, CircularProgress} from "@mui/material";
import RequiredBox from "../component/RequiredBox";
import MultiSelect from "../component/MultiSelect";
import AlertsDialog from "../component/AlertsDialog";
import AlertsSnackbar from "../component/AlertsSnackbar";
import { useLocation } from 'react-router-dom';
import { MakerData } from "../types/type";
import { useRecoilValue } from "recoil";
import { CurrentPosition } from "../store/currentState";
import { useNavigate } from "react-router-dom";
import { getCategoriesApi, getPointApi, setPlaceApi } from "../services/api/api";
import { mapDistance } from "../constants/constants";
import { useCookies } from "react-cookie";
import MyBottomNavigation from "../component/MyBottomNavigation";
import { useTranslation } from "react-i18next";

function AddPlace(): JSX.Element {
  const { t } = useTranslation();
  const [cookies, setCookie, removeCookie] = useCookies(['recotech_mapapps_token']);
  const currentPosition = useRecoilValue(CurrentPosition);
  const navigate = useNavigate();
  const location = useLocation();
  const [categoryNameAll, setCategoryNameAll] = useState<string[]>([]);
  const [point, setPoint] = useState<number>(0);
  const [countryIdForSearch, setCountryIdForSearch] = useState<number>(1); // 初期値を1（日本）に設定

  //Selectの設定
  const [openSelect, setOpenSelect] = useState<boolean>(false);

  //api情報を読み込みセットする
  const fetchData = async (latitude: number, longitude: number) => {
      if (!cookies.recotech_mapapps_token) navigate("/");

      let country_id = 1; // デフォルトは1（マレーシア以外）
      if (typeof google !== 'undefined' && google.maps && google.maps.Geocoder) {
        const geocoder = new google.maps.Geocoder();
        try {
          const response = await geocoder.geocode({ location: { lat: latitude, lng: longitude } });
          if (response.results && response.results[0]) {
            const addressComponents = response.results[0].address_components;
            const countryComponent = addressComponents.find(component => component.types.includes("country"));
            if (countryComponent) {
              const countryCode = countryComponent.short_name;
              // マレーシアかどうかをチェック
              if (countryCode === 'MY') {
                  country_id = 2; // マレーシアの場合は ID 2 を設定
              }
              setCountryIdForSearch(country_id); //国別検索用にセット
              console.log("Determined country_id:", country_id, "based on countryCode:", countryCode); // デバッグ用
            }
          }
        } catch (error) {
          console.error("Geocoding failed:", error);
          setAlertsMessage(t("pages.addPlace.geocodingError")); // エラーメッセージ用の翻訳キーを追加想定
          setSeverity("error");
          setOpenSnackbar(true);
          // エラーが発生した場合でも、デフォルトの country_id = 1 で続行するか、処理を中断するか検討
        }
      }

      const response = await getCategoriesApi(setAlertsMessage,setSeverity,setOpenSnackbar, country_id); // 取得した country_id を渡す
      console.log("getCategoriesApi response:", response); // APIレスポンスをログ出力
      const categoryNameAll = response.map((category) => { 
        if (!category || typeof category.name === 'undefined') {
          console.error('Invalid category object received:', category);
          return null; // or some default value, or skip
        }
        return category.name 
      }).filter(name => name !== null); // nullを除外
      console.log("Setting categoryNameAll:", categoryNameAll); // セットする値をログ出力
      setCategoryNameAll(categoryNameAll as string[])

      // setCategoryNameAll が2回呼ばれているのは不要なので削除
      // setCategoryNameAll(categoryNameAll)
  };

  useEffect(() => {
    if (!cookies.recotech_mapapps_token) navigate("/");

    let latToFetch: number | null = null;
    let lngToFetch: number | null = null;

    // 1. location.state から緯度経度を取得試行
    if (location.state) {
        const markerFromState = location.state as MakerData;
        if (markerFromState.latitude && markerFromState.longitude) {
            latToFetch = markerFromState.latitude;
            lngToFetch = markerFromState.longitude;
            setLat(latToFetch); // フォームの初期値も設定
            setLng(lngToFetch); // フォームの初期値も設定
        }
    }

    // 2. location.state に有効な緯度経度がない場合、currentPosition を使用
    if (latToFetch === null || lngToFetch === null) {
        if (currentPosition.lat !== 0 && currentPosition.lng !== 0) {
            latToFetch = currentPosition.lat;
            lngToFetch = currentPosition.lng;
            // currentPosition を使う場合、フォームの初期値は setLat/setLng しない (編集中の場所ではないため)
        }
    }

    // 3. 有効な緯度経度が得られた場合のみ fetchData を実行
    if (latToFetch !== null && lngToFetch !== null) {
        fetchData(latToFetch, lngToFetch);
    } else {
        // 有効な緯度経度が得られない場合のエラーハンドリングやリダイレクト
        console.warn("Could not determine location for AddPlace. Redirecting.");
        // navigate("/"); // 必要に応じてリダイレクト
    }
    // 翻訳関数 t も依存配列に追加
  }, [cookies.recotech_mapapps_token, navigate, currentPosition, location.state, t]);

  const [isWaitingResponse, setIsWaitingResponse] = useState<boolean>(false);

  const [searchText, setSearchText] = useState<string | null>("");
  const autoCompleteRef = useRef<google.maps.places.Autocomplete>();
  const inputRef = useRef();

  useEffect(() => {
    try{
      if(google){
        autoCompleteRef.current = new window.google.maps.places.Autocomplete(
          inputRef.current!,
          {
            strictBounds: true,
            bounds:  new google.maps.LatLngBounds(
              new google.maps.LatLng(currentPosition.lat-mapDistance, currentPosition.lng-mapDistance), // 南西端の座標　0.01で約1km
              new google.maps.LatLng(currentPosition.lat+mapDistance, currentPosition.lng+mapDistance) // 北東端の座標　0.01で約1km
            ),
            componentRestrictions: { country: countryIdForSearch === 2 ? "my" : "jp"},
            fields: ["address_components", "formatted_address", "name", "geometry"],
            types: ["establishment"],
          }
        );
        autoCompleteRef.current.addListener('place_changed', () => {
          const res = autoCompleteRef.current?.getPlace();
          setName(res?.name ?? "")
          setAddress(res?.formatted_address?.substr(13) ?? "")
          setZipCode(res?.address_components?.slice(-1)[0]?.long_name ?? "")
          setSearchText("")
          setLat(res?.geometry?.location?.lat()!)
          setLng(res?.geometry?.location?.lng()!)
        });
      }else{
        navigate("/");        
      }
    }catch(e){
      navigate("/");
    }
  }, [countryIdForSearch, currentPosition.lat, currentPosition.lng, mapDistance, navigate]);

  //Marker情報の設定は、recoilで受け取ると新規の場合でも何か情報が入ってしまう可能性がある。。
  //そのため、useLocationで受け取ることとする。その時に、idがあれば既存情報の修正、無ければ新規登録とする。
  const marker = location.state as MakerData;
  const [id, setId] = useState<number | null>(null);
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);

  const [name, setName] = useState<string | null>("");

  const [files, setFiles] = useState<(File & {preview:string})[]>([]);
  const {getRootProps, getInputProps} = useDropzone({
    accept: {
      'image/*': []
    },
    maxFiles: 1,
    multiple: false,
    onDrop: acceptedFiles => {
      setFiles(acceptedFiles.map(file => Object.assign(file, {
        preview: URL.createObjectURL(file)
      })));
    }
  });

  const [zipCode, setZipCode] = useState<string | null>("");
  const [address, setAddress] = useState<string | null>("");

  const [phoneNumber, setPhoneNumber] = useState<string | null>("");
  const [whatsappNumber, setWhatsappNumber] = useState<string>("");
  const [categoryNames, setCategoryNames] = useState<string[]>([]);
  const [additionalCategoryNames, setAdditionalCategoryNames] = useState<string>("");
  const [description, setDescription] = useState<string | null>("");
  const [regularHolidays, setRegularHolidays] = useState<string | null>("");
  const [businessHours, setBusinessHours] = useState<string | null>("");

  //SnackBarの設定
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [alertsMessage, setAlertsMessage] = useState<string>("");
  const [severity, setSeverity] = useState< "error" | "success" >("error");

  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const handleAgreeClick = (async () => {
    const formData = new FormData();
    formData.append("recovery_place[name]", name??"");
    formData.append("recovery_place[zip_code]", zipCode??"");
    formData.append("recovery_place[address]", address??"");
    formData.append("recovery_place[phone_number]", phoneNumber??"");
    if(categoryNames.length > 0){
      categoryNames?.forEach((categoryName) => {
        formData.append("recovery_place[category_names][]", categoryName);      
      });  
    }
    if(additionalCategoryNames !== ""){
      formData.append("recovery_place[additional_category_names]", additionalCategoryNames??"");
    }
    formData.append("recovery_place[whatsapp_number]", whatsappNumber??"");
    formData.append("recovery_place[description]", description??"");    
    formData.append("recovery_place[business_hours]", businessHours??"");    
    formData.append("recovery_place[regular_holidays]", regularHolidays??"");
    formData.append("recovery_place[latitude]", lat?.toString()??"");
    formData.append("recovery_place[longitude]", lng?.toString()??"");
    formData.append("recovery_place[image]", files[0]??"");
    formData.append("recovery_place[recovery_place_id]", id?.toString()??"");
    const res = await setPlaceApi(cookies.recotech_mapapps_token,formData,setAlertsMessage,setSeverity,setOpenSnackbar,setIsWaitingResponse)
    if(res){
      //登録後の処理
      setName("")
      setZipCode("")
      setAddress("")
      setPhoneNumber("")
      setWhatsappNumber("")
      setCategoryNames([])
      setAdditionalCategoryNames("")
      setDescription("")
      setRegularHolidays("")
      setBusinessHours("")
      setFiles([])
      setLat(null)
      setLng(null)
    }else{
      //登録失敗の処理
    }
    setOpenDialog(false)
  })

  const handleSendClick = (() => {
    if(name === ""){
      setAlertsMessage(t("pages.addPlace.nameRequired"))
      setSeverity("error")
      setOpenSnackbar(true)
      return
    }
    if(address === ""){
      setAlertsMessage(t("pages.addPlace.addressRequired"))
      setSeverity("error")
      setOpenSnackbar(true)
      return
    }

    //空行や前後の空白を削除
    let formattedAdditionalCategoryNames = additionalCategoryNames.trim();
    formattedAdditionalCategoryNames = formattedAdditionalCategoryNames.replace(/(\r?\n)+/g,"\n");

    // 新たに追加されたカテゴリ名を改行で分割し、トリミングして配列に変換
    const additionalCategories = formattedAdditionalCategoryNames.split("\n").map(s => s.trim());

    // 重複しない追加カテゴリ名と、既存のカテゴリに追加する名前を分けて保持する配列
    let uniqueAdditionalCategories: string[] = [];
    let newCategoryNames: string[] = [];
    
    additionalCategories.forEach(category => {
      if (categoryNameAll.includes(category)) {
        // 既存のカテゴリ名に含まれる場合、新しいカテゴリ名に追加（ただし、すでに存在しない場合のみ）
        if (!categoryNames.includes(category)) {
          newCategoryNames.push(category);
        }
      } else {
        // 重複しない場合はuniqueAdditionalCategoriesに追加
        uniqueAdditionalCategories.push(category);
      }
    });

    // 配列を改行で結合し、文字列に戻す
    const updatedAdditionalCategoryNames = uniqueAdditionalCategories.join("\n");

    // 状態を更新
    setAdditionalCategoryNames(updatedAdditionalCategoryNames);
    setCategoryNames([...categoryNames, ...newCategoryNames]);
    
    if(categoryNames!.length === 0 && formattedAdditionalCategoryNames === ""){
      setAlertsMessage(t("pages.addPlace.itemRequired"))
      setSeverity("error")
      setOpenSnackbar(true)
      return
    }

    setOpenDialog(true)
  })

  useEffect(() => {
    if (marker !== null) {
      setId(marker.id?marker.id:null)
      setLat(marker.latitude?marker.latitude:null)
      setLng(marker.longitude?marker.longitude:null)
      setName(marker.name?marker.name:null)
      if(marker.imageUrl){
        if(marker.imageUrl !== 'noimage.jpeg'){
          fetch(marker.imageUrl)
          .then(response => response.blob())
          .then(blob => new File([blob], "image.jpg"))
          .then(file => {
            setFiles([Object.assign(file, {
              preview: URL.createObjectURL(file)
            })]);  
          })  
        }
      }
      setCategoryNames(marker.categoryNames)
      setZipCode(marker.zipCode?marker.zipCode:null)
      setAddress(marker.address?marker.address:null)
      setPhoneNumber(marker.phoneNumber?marker.phoneNumber:null)
      setDescription(marker.description?marker.description:null)
      setRegularHolidays(marker.regularHolidays?marker.regularHolidays:null)
      setBusinessHours(marker.businessHours?marker.businessHours:null)      
    }
    // メモリリークを防ぐため、データURIを必ず失効させること、アンマウント時に実行すること
    return () => files.forEach(file => URL.revokeObjectURL(URL.createObjectURL(file)));
  }, []);

  return (
    <>
      {isWaitingResponse ?
        <Backdrop open={isWaitingResponse} sx={{zIndex:10000}}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          width="100%"
        >
          <CircularProgress color='inherit' />
          <Typography component="div" variant="caption" sx={{color:"white",fontWeight:"bold"}}>
            {t("pages.addPlace.sending")}
          </Typography>
        </Box>
      </Backdrop>
      :<></>
      }
      <AlertsSnackbar
        openSnackbar={openSnackbar}
        setOpenSnackbar={setOpenSnackbar}
        vertical={"top"}
        severity={severity}
        message={alertsMessage}
      />
      <AlertsDialog
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        handleAgreeClick={()=>handleAgreeClick()}
        dialogTitle={t("pages.addPlace.registerRequest")}
        dialogMessage={t("pages.addPlace.confirmSend")}
        isWaitingResponse={isWaitingResponse}
      />
      <Header/>
        <Box 
          sx={{ p: 3 }}
          display="flex"
          alignItems="start"
          justifyContent="start"
          flexDirection="column"
        >
          <Typography component="div" variant="h5" sx={{fontWeight:"bold"}}>
            {t("pages.addPlace.title")}
          </Typography>
          <Typography component="div" variant="caption">
            　
          </Typography>
          <Typography component="div" variant="caption">
            {t("pages.addPlace.description1")}
          </Typography>
          <Typography component="div" variant="caption">
            {t("pages.addPlace.description2")}
          </Typography>
          <Typography component="div" variant="caption">
            {t("pages.addPlace.description3")}
          </Typography>
          <Typography component="div" variant="caption">
            {t("pages.addPlace.description4")}
          </Typography>
          
          {(lat && lng)? //新規登録でない場合はキーワード検索は非表示にしておく
          <></>
          :
          <>
            <Box mt={2} display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"start"} >
              <Typography component="div" variant="h6" sx={{fontWeight:"bold"}}>
                {t("pages.addPlace.keywordSearch")}
              </Typography>
            </Box>
            <FormControl>
              <TextField
                  style={{width: "85vw", marginTop: "10px"}}
                  variant="outlined"            
                  fullWidth={true}
                  type={'text'}
                  value={searchText}
                  onChange={(e)=>{
                    setSearchText(e.target.value)
                  }}  
                  inputRef={inputRef}
                  helperText={t("pages.addPlace.notFoundInstruction")}
              />
            </FormControl>
          </>
          }

          <Box mt={2} display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"start"} >
            <Typography component="div" variant="h6" sx={{fontWeight:"bold"}}>
              {t("pages.addPlace.pointName")}
            </Typography>
            <RequiredBox/>
          </Box>
          <FormControl>
            <TextField
              style={{width: "85vw", marginTop: "10px"}}
              variant="outlined"            
              fullWidth={true}
              type={'text'}
              value={name}
              onChange={(e)=>{
                setName(e.target.value)
              }}  
            />
          </FormControl>

          <Box mt={2} display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"start"} >
            <Typography component="div" variant="h6" sx={{fontWeight:"bold"}}>
              {t("pages.addPlace.zipCode")}
            </Typography>
            <RequiredBox/>
          </Box>
          <FormControl>
            <TextField
              disabled={true}
              placeholder={t("pages.addPlace.zipCodeAuto")}
              style={{width: "85vw", marginTop: "10px"}}
              variant="outlined"            
              fullWidth={true}
              type={'text'}
              value={zipCode}
              onChange={(e)=>{
                setZipCode(e.target.value)
              }}  
            />
          </FormControl>

          <Box mt={2} display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"start"} >
            <Typography component="div" variant="h6" sx={{fontWeight:"bold"}}>
              {t("pages.addPlace.address")}
            </Typography>
            <RequiredBox/>
          </Box>
          <FormControl>
            <TextField
              disabled={true}
              placeholder={t("pages.addPlace.zipCodeAuto")}
              style={{width: "85vw", marginTop: "10px"}}
              variant="outlined"            
              fullWidth={true}
              type={'text'}
              value={address}
              onChange={(e)=>{
                setAddress(e.target.value)
              }}  
            />
          </FormControl>

          <Box mt={2} display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"start"} >
            <Typography component="div" variant="h6" sx={{fontWeight:"bold"}}>
              {t("pages.addPlace.phoneNumber")}
            </Typography>
          </Box>
          <FormControl>
            <TextField
              style={{width: "85vw", marginTop: "10px"}}
              variant="outlined"            
              fullWidth={true}
              type={'text'}
              value={phoneNumber}
              onChange={(e)=>{
                setPhoneNumber(e.target.value)
              }}  
            />
          </FormControl>

          <Box mt={2} display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"start"} >
            <Typography component="div" variant="h6" sx={{fontWeight:"bold"}}>
              {t("pages.addPlace.items")}
            </Typography>
            <RequiredBox/>
          </Box>
          <FormControl sx={{width: "85vw" ,marginTop: "10px"}}>
            <MultiSelect
              categoryNames={categoryNames!}
              setCategoryNames={setCategoryNames}
              categoryNameAll={categoryNameAll}
              openSelect={openSelect}
              setOpenSelect={setOpenSelect}  
            />
            <FormHelperText>{t("pages.addPlace.itemsNotFound")}</FormHelperText>
          </FormControl>
          <FormControl>
            <TextField
              multiline
              maxRows={5}
              style={{width: "85vw", marginTop: "10px"}}
              variant="outlined"            
              fullWidth={true}
              type={'text'}
              value={additionalCategoryNames}
              onChange={(e)=>{
                setAdditionalCategoryNames(e.target.value)
              }}  
            />
            <FormHelperText>{t("pages.addPlace.multipleItems")}</FormHelperText>
          </FormControl>


          <Box mt={2} display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"start"} >
            <Typography component="div" variant="h6" sx={{fontWeight:"bold"}}>
              {t("pages.addPlace.description")}
            </Typography>
          </Box>
          <FormControl>
            <TextField
              multiline
              maxRows={5}
              style={{width: "85vw", marginTop: "10px"}}
              variant="outlined"            
              fullWidth={true}
              type={'text'}
              value={description}
              onChange={(e)=>{
                setDescription(e.target.value)
              }}  
            />
          </FormControl>

          <Box mt={2} display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"start"} >
            <Typography component="div" variant="h6" sx={{fontWeight:"bold"}}>
              {t("pages.addPlace.businessHours")}
            </Typography>
          </Box>
          <FormControl>
            <TextField
              multiline
              maxRows={5}
              style={{width: "85vw", marginTop: "10px"}}
              variant="outlined"            
              fullWidth={true}
              type={'text'}
              value={businessHours}
              onChange={(e)=>{
                setBusinessHours(e.target.value)
              }}  
            />
          </FormControl>

          <Box mt={2} display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"start"} >
            <Typography component="div" variant="h6" sx={{fontWeight:"bold"}}>
              {t("pages.addPlace.closedDays")}
            </Typography>
          </Box>
          <FormControl>
            <TextField
              style={{width: "85vw", marginTop: "10px"}}
              variant="outlined"            
              fullWidth={true}
              type={'text'}
              value={regularHolidays}
              onChange={(e)=>{
                setRegularHolidays(e.target.value)
              }}  
            />
          </FormControl>

          <Box mt={2} display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"start"} >
            <Typography component="div" variant="h6" sx={{fontWeight:"bold"}}>
              {t("pages.addPlace.photos")}
            </Typography>
          </Box>
          <Box
            width="85vw"
            marginTop="10px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"  
          >
            {(files.length == 0)?
            <Box {...getRootProps()}
            >
              <input {...getInputProps()} />
              <img src="upload.png" width={"200px"}/>
            </Box>
            :
            <Box
              marginTop="10px"
              marginBottom="10px"
              onClick={() => {
                setFiles([])
              }}
            >
            {
              files.map(file => (
                <Box key={file.name}>
                  <img
                    src={file.preview}
                    width={"250px"}
                    onLoad={() => { URL.revokeObjectURL(file.preview)}}
                  />
                </Box>
              ))
            }
            </Box>
            }
            {(files.length > 0)?<>
              <Typography component="div" variant="caption" sx={{marginTop:2}}>{t("pages.addPlace.photoInstruction")}</Typography>            
            </>:<></>}
          </Box>

          <Button
            variant="contained"
            style={{width: "85vw", marginTop: "30px" ,backgroundColor: "#4B4B4B", color: "white",borderRadius: "20px"}}
            onClick={() => {
              handleSendClick()
            }}
          >
            {t("pages.addPlace.submit")}
          </Button>
          <Box mt={10}></Box>
        </Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          width="100%"
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
          }}
        >
          <MyBottomNavigation />
        </Box>
    </>
  );
};
export default AddPlace;
